import React, { useState, useEffect } from 'react';
import { Language } from '../types';
import { TRANSLATIONS } from '../data/translations';
import { getSiteConfig, SiteConfig, getLogoSizeClass } from '../data/siteConfig';
import { getLogoImageSeo } from '../utils/imageSeo';
import { Phone, Globe, Menu, X, Shield, Wrench, Flame, ShieldAlert, MapPin, Info, Mail } from 'lucide-react';

interface HeaderProps {
  currentLang: Language;
  setLang: (lang: Language) => void;
  currentView: string;
  setView: (view: string) => void;
  onOpenSchema?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ currentLang, setLang, currentView, setView, onOpenSchema }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [config, setConfig] = useState<SiteConfig>(getSiteConfig());
  const t = TRANSLATIONS[currentLang];

  useEffect(() => {
    const handleConfigUpdate = () => {
      setConfig(getSiteConfig());
    };
    window.addEventListener('siteConfigUpdated', handleConfigUpdate);
    return () => window.removeEventListener('siteConfigUpdated', handleConfigUpdate);
  }, []);

  const navItems = [
    { id: 'home', label: t.navHome },
    { id: 'services', label: t.navServices },
    { id: 'zones', label: t.navZones },
    { id: 'about', label: t.navAbout },
    { id: 'contact', label: t.navContact },
  ];

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
      {/* Main Navbar matching Sleek Interface spec */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center min-h-[5rem] py-2.5">
          {/* Logo */}
          <button
            onClick={() => setView('home')}
            aria-label="API HEAT - Retour à l'accueil"
            className="flex items-center gap-3.5 text-left group focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-2xl"
          >
            {config.headerLogo ? (
              <img
                src={config.headerLogo}
                alt={getLogoImageSeo(currentLang).alt}
                title={getLogoImageSeo(currentLang).title}
                width="179"
                height="179"
                loading="eager"
                decoding="async"
                className={`${getLogoSizeClass(config.headerLogoSize)} rounded-2xl object-contain bg-white border border-slate-200 p-1 shadow-sm group-hover:scale-105 transition shrink-0`}
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className={`${getLogoSizeClass(config.headerLogoSize)} rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-md shrink-0`}>
                <Flame className="w-1/2 h-1/2 text-yellow-300" />
              </div>
            )}

          </button>

          {/* Desktop Navigation & Actions */}
          <nav className="hidden lg:flex items-center gap-8">
            <div className="flex items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setView(item.id)}
                  aria-current={currentView === item.id ? 'page' : undefined}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                    currentView === item.id
                      ? 'bg-orange-100 text-orange-800 border border-orange-200 shadow-sm'
                      : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Language Switcher Pill */}
            <div className="flex bg-slate-100 p-1 rounded-full text-xs font-bold border border-slate-300 shadow-inner" role="group" aria-label="Sélection de la langue">
              <button
                onClick={() => setLang('fr')}
                aria-label="Passer le site en Français"
                aria-pressed={currentLang === 'fr'}
                className={`px-3.5 py-1.5 rounded-full transition-all min-h-[36px] ${currentLang === 'fr' ? 'bg-slate-900 text-white shadow font-black' : 'text-slate-700 hover:text-slate-950 font-bold'}`}
              >
                FR
              </button>
              <button
                onClick={() => setLang('nl')}
                aria-label="Schakel site over naar Nederlands"
                aria-pressed={currentLang === 'nl'}
                className={`px-3.5 py-1.5 rounded-full transition-all min-h-[36px] ${currentLang === 'nl' ? 'bg-slate-900 text-white shadow font-black' : 'text-slate-700 hover:text-slate-950 font-bold'}`}
              >
                NL
              </button>
              <button
                onClick={() => setLang('en')}
                aria-label="Switch site to English"
                aria-pressed={currentLang === 'en'}
                className={`px-3.5 py-1.5 rounded-full transition-all min-h-[36px] ${currentLang === 'en' ? 'bg-slate-900 text-white shadow font-black' : 'text-slate-700 hover:text-slate-950 font-bold'}`}
              >
                EN
              </button>
            </div>

            {/* Emergency Call Button */}
            <a
              href={`tel:${config.gsm.replace(/[^0-9+]/g, '')}`}
              aria-label={`Appeler le service d'urgence 24/7 au ${config.gsm}`}
              className="flex items-center gap-3 bg-slate-900 text-white px-6 py-3 rounded-full hover:bg-slate-800 transition-colors shadow-md min-h-[44px]"
            >
              <svg className="w-4 h-4 text-orange-400 shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true"><path d='M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z'></path></svg>
              <span className="font-bold uppercase tracking-wide text-xs">{t.ctaCallEmergency} : {config.gsm}</span>
            </a>
          </nav>

          {/* Mobile Menu & Call */}
          <div className="flex items-center gap-2 lg:hidden">
            <div className="flex bg-slate-100 p-0.5 rounded-full text-xs font-bold border border-slate-300" role="group" aria-label="Sélection de la langue">
              <button
                onClick={() => setLang('fr')}
                aria-label="Français"
                className={`px-3 py-1.5 rounded-full min-w-[36px] min-h-[36px] flex items-center justify-center font-bold ${currentLang === 'fr' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-700'}`}
              >
                FR
              </button>
              <button
                onClick={() => setLang('nl')}
                aria-label="Nederlands"
                className={`px-3 py-1.5 rounded-full min-w-[36px] min-h-[36px] flex items-center justify-center font-bold ${currentLang === 'nl' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-700'}`}
              >
                NL
              </button>
              <button
                onClick={() => setLang('en')}
                aria-label="English"
                className={`px-3 py-1.5 rounded-full min-w-[36px] min-h-[36px] flex items-center justify-center font-bold ${currentLang === 'en' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-700'}`}
              >
                EN
              </button>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Fermer le menu de navigation" : "Ouvrir le menu de navigation"}
              aria-expanded={mobileMenuOpen}
              className="p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-slate-800 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 pt-3 pb-6 space-y-2 shadow-xl animate-fadeIn">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setView(item.id);
                setMobileMenuOpen(false);
              }}
              className={`w-full text-left px-4 py-3 rounded-xl text-base font-semibold transition ${
                currentView === item.id
                  ? 'bg-orange-50 text-orange-600 font-bold'
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              {item.label}
            </button>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            <a
              href="tel:+32493373789"
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-slate-900 text-white rounded-xl font-bold text-sm shadow-md"
            >
              <Phone className="w-4 h-4 text-orange-500" />
              <span>{t.ctaCallEmergency} : 0493 37 37 89</span>
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
